import { PageSize } from '@/constant';
import {
	GetWorkerUserListParams,
	IWorkerUser,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { formatWorkerUserList, RootState } from '..';
import {
	IPayloadCreateWorkerUserRequest,
	IPayloadDeleteWorkerUserRequest,
	IPayloadGetWorkerUserDetailRequest,
	IPayloadGetWorkerUserDetailSuccess,
	IPayloadGetWorkerUserListRequest,
	IPayloadGetWorkerUserListSuccess,
	IWorkerUserDetailStore,
} from './worker-user.type';

const InitParams: GetWorkerUserListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	workerUserList: [] as IWorkerUserDetailStore[],
	total: 0,
	params: InitParams,
	workerUserDetail: {} as IWorkerUserDetailStore,
};

const workerUserSlice = createSlice({
	initialState,
	name: 'workerUser',
	reducers: {
		/**---- GET SYSTEM_USER LIST START ---- */
		getWorkerUserListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerUserListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getWorkerUserListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerUserListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerUserList = content;
			state.total = total;
		},
		getWorkerUserListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerUserList = [];
		},
		/**---- GET SYSTEM_USER LIST END ---- */

		/**---- CREATE SYSTEM_USER START ---- */
		createWorkerUserRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerUserRequest>
		) => {
			state.fetching = true;
		},
		createWorkerUserSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerUserFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE SYSTEM_USER END ---- */

		/**---- DELETE SYSTEM_USER START ---- */
		deleteWorkerUserRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerUserRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerUserSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerUserFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE SYSTEM_USER START ---- */

		/**---- GET SYSTEM_USER DETAIL START ---- */
		getWorkerUserDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerUserDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerUserDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerUserDetailSuccess>
		) => {
			const { workerUserDetail } = action.payload;
			state.fetching = false;
			state.workerUserDetail = workerUserDetail;
		},
		getWorkerUserDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET SYSTEM_USER DETAIL END ---- */

		/**---- GET SYSTEM NOTICE DETAIL START ---- */
		updateWorkerUserSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerUser>>
		) => {
			const { data } = action.payload;
			const workerUserList = [...state.workerUserList];
			state.workerUserList = formatWorkerUserList(
				mergeListItemUpdated<IWorkerUserDetailStore, IWorkerUser>(
					workerUserList,
					data
				)
			);
		},
		updateWorkerUserDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerUser>>
		) => {
			const { data } = action.payload;
			const workerUserDetail = { ...state.workerUserDetail };
			state.workerUserDetail = mergeItemUpdated(workerUserDetail, data);
		},
		/**---- GET SYSTEM NOTICE DETAIL END ---- */
	},
});

export const WorkerUserSelectors = {
	selectFetching: (state: RootState) => state.workerUser.fetching,
	selectParams: (state: RootState) => state.workerUser.params,
	selectList: (state: RootState) => state.workerUser.workerUserList,
	selectTotal: (state: RootState) => state.workerUser.total,
	selectDetail: (state: RootState) => state.workerUser.workerUserDetail,
};

export const WorkerUserActions = workerUserSlice.actions;
export const WorkerUserReducer = workerUserSlice.reducer;
